import React, { useState, useMemo } from 'react';
import { useHistory, Route } from 'react-router-dom';
import classes from 'classnames';
import { IconButton, CommandBar, FontSizes, FontWeights, mergeStyleSets, Panel, PanelType } from '@fluentui/react';
import WhiteCommandBarButton from 'src/components/base/WhiteCommandBarButton';
import { CloseDialog } from 'src/components/base/ConfirmDialog';


const classNames = mergeStyleSets({
  header: {
    width: '100%',
    maxWidth: '100vw',
    boxSizing: 'border-box',
    height: 44,
    display: 'flex',
    alignItems: 'center',
    // paddingRight: 8,
    padding: '0px 24px 0px 8px',
    borderBottom: '1px solid #A5A5A5',
    // position: 'fixed',
    // top: 0,
    zIndex: '100',
    backgroundColor: 'white'
  },
  fixed: {
    position: 'fixed',
    top: 0,
  },
  title: {
    flexGrow: 1,
    fontSize: FontSizes.large,
    fontWeight: FontWeights.semilight,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  content: {
    paddingTop: 44,
  },
  commandBar: {
    border: 'inherit',
  }
})


const Page = ({ title, component, backTo = '/', path, ...rest }) => {
  const history = useHistory();

  const pageState = usePageState({ title });
  const { setIsDialogHidden, dirty } = pageState;

  const goBack = () => {
    setIsDialogHidden(true);
    backTo
      ? history.replace(backTo)
      : history.goBack();
  }

  const handleGoBack = () => {
    dirty
      ? setIsDialogHidden(false)
      : goBack();
  }

  const renderProps = { ...pageState, goBack, handleGoBack, isFixed: true };

  return (
    <Route path={path} render={() => (
      <div>
        <Title {...renderProps} />

        <div className={classNames.content}>
          { renderComponent(renderProps, component, { ...rest, history, goBack }) }
          <DirtyDialog {...renderProps} />
        </div>
      </div>
    )} />
  ); 
}


const ModalPage = ({ title, isOpen, onClose, component, helpLink,...rest }) => {
  const pageState = usePageState({ title, helpLink });
  const { setIsDialogHidden, dirty } = pageState;

  const goBack = () => {
    setIsDialogHidden(true);
    onClose();
  }

  const handleGoBack = () => {
    dirty ? setIsDialogHidden(false) : goBack();
  }

  const renderProps = { ...pageState, goBack, handleGoBack, isFixed: false };

  return (
    <Panel
      key={'Panel'}
      isOpen={isOpen}
      isFooterAtBottom={true}
      type={PanelType.smallFluid}
      onRenderNavigationContent={() => <Title {...renderProps } />}
      styles={{
        content: {
          padding: 0,
        }
      }}
      >
      { renderComponent(renderProps, component, { ...rest, goBack }) }
      <DirtyDialog {...renderProps} />
    </Panel>
  )
}


function usePageState({ title: initialTitle, helpLink, ...rest }) {
  const [commands, setCommands] = useState([]);
  const [dirty, setDirty] = useState(null);
  const [title, setTitle] = useState(initialTitle);
  const [isDialogHidden, setIsDialogHidden] = useState(true);

  const createHelpButton = (link) => {
      return {
        key: 'help-link',
        iconProps: { iconName: 'Help' },
        onClick: () => window.open(link, '_blank')
      }
  }
  const [helpButton, setHelpButton] = useState(helpLink ? createHelpButton(helpLink) : null);

  const setHelpLink = (link) => {
    setHelpButton(link ?  createHelpButton(link) : null)
  };

  const commandBarButtons = useMemo(() => [helpButton, ...commands].filter(Boolean), [helpButton, commands]);
  return { 
    commands, setCommands, 
    dirty, setDirty, 
    title, setTitle, 
    isDialogHidden, setIsDialogHidden, 
    setHelpLink, commandBarButtons, ...rest
  };
}

function renderComponent(renderProps, Component, componentProps ) {
  const { setCommands, setDirty, setTitle, setHelpLink } = renderProps;
  const pageControl = { setHeaderCommands: setCommands, setDirtyActions: setDirty, setTitle, setHelpLink };
  return (<Component pageControl={pageControl} {...componentProps} />
  );
}

function Title({ title, commandBarButtons, handleGoBack, isFixed }) {
  return (
    <div className={classes(classNames.header, { [classNames.fixed]: isFixed })} >
      <IconButton iconProps={{ iconName: 'ChevronLeft' }} onClick={handleGoBack} />
      <div className={classNames.title}>{title}</div>
      <CommandBar
        className={classNames.commandBar}
        styles={{ root: { backgroundColor: 'white' }}}
        farItems={commandBarButtons}
        buttonAs={WhiteCommandBarButton}
      />
    </div>
  );
}

function DirtyDialog({ dirty, setIsDialogHidden, isDialogHidden, goBack }) {
  const { disableSave, onSave, onForget, text } = dirty ?? {};
  return (
    <>
    { dirty && <CloseDialog
      text={text}
      hidden={isDialogHidden}
      onDismiss={() => setIsDialogHidden(true)}
      onForget={async () => { await onForget?.(); goBack(); }}
      disableSave={disableSave}
      onSave={async () => { await onSave(); goBack(); }}
      />
    }
  </>);
}


export { ModalPage, Page };
export default Page;